import _ from 'lodash';
import { useRef } from 'react';
import { useConfigAPI } from '../../apis/config-api';
import { useLanguageAPI } from '../../apis/language-api';
import { useMenuAPI } from '../../apis/menu-api';
import { useOrderAPI } from '../../apis/order-api';
import { useCloudinary } from '../../hooks/use-cloudinary';
import { useStorage } from '../../hooks/use-storage';
import { wait } from '../wait';
import { preloadImages } from './image-manager';
import { selectLanguage } from './language-manager';
import { loadMenuFonts as loadMenuFontsFromManager } from './menu-font-manager';
import { isDevEnv } from './host-manager';

const menuLoadingDelay = isDevEnv ? 0 : 2800;

function useMenuManager() {
  const { loadMenuFromRemote, getMenuImageFromRemote } = useMenuAPI();
  const { findSpotOrdersFromRemote } = useOrderAPI();
  const { findLanguagesFromRemote } = useLanguageAPI();
  const { loadConfigFromRemote } = useConfigAPI();
  const { transform } = useCloudinary();

  const {
    setOrders,
    setMenuSlug,
    menuSlugRef,
    setMenuId,
    menuIdRef,
    setMenuInfo,
    menuInfoRef,
    setMenuLayout,
    menuLayoutRef,
    setMenuCategories,
    menuCategoriesRef,
    configRef,
    spotRef,
    setConfig,
    setSpot,
    setLoadedSpot,
    setLanguages,
    languageRef,
    setLanguage,
  } = useStorage();
  const loadingRef = useRef(false);

  function setMenu(menu) {
    const rCategories = menu.categories;
    const rSlug = menu.info.slug;
    const rId = menu.id;
    const rLayout = menu.info.layout;
    const rInfo = menu.info;

    delete rInfo.slug;
    delete rInfo.layout;

    if (!_.isEqual(rSlug, menuSlugRef.current)) {
      setMenuSlug(rSlug);
    }

    if (!_.isEqual(rId, menuIdRef.current)) {
      setMenuId(rId);
    }

    if (!_.isEqual(rCategories, menuCategoriesRef.current)) {
      setMenuCategories(rCategories);
    }

    if (!_.isEqual(rLayout, menuLayoutRef.current)) {
      setMenuLayout(rLayout);
    }

    if (!_.isEqual(rInfo, menuInfoRef.current)) {
      setMenuInfo(rInfo);
    }
  }

  async function loadMenuImages() {
    const urls =
      menuCategoriesRef.current
        ?.filter((c) => c.image?.url != null)
        ?.map((c) => transform(c.image.url, 2)) ?? [];

    urls.push(getMenuLogo().url);
    urls.push(getMenuTitleBackgroundUrl());

    preloadImages(urls);

    // // eslint-disable-next-line no-undef
    // window.preloadedImages = urls.map((url) => {
    //   // eslint-disable-next-line no-undef
    //   const image = new Image();
    //   image.src = url;

    //   return image;
    // });

    const promises = urls
      .map((url) => getMenuImageFromRemote(url))
      .filter((url) => !_.isEmpty(url));

    try {
      await Promise.all(promises);
      return true;
    } catch (error) {
      console.log(error);
    }

    return false;
  }

  async function loadMenuFonts() {
    await loadMenuFontsFromManager(menuLayoutRef.current);
  }

  async function loadMenu(
    {
      includesImages,
      includesFonts,
      includesOrders,
      includesLanguages,
      shouldWait,
    } = {
      includesImages: false,
      includesFonts: false,
      includesOrders: false,
      includesLanguages: false,
      shouldWait: false,
    }
  ) {
    if (loadingRef.current) return;
    loadingRef.current = true;

    if (includesLanguages) {
      await loadLanguage();
    }

    const response = await loadMenuFromRemote();
    const rMenu = parseMenu(response.data);
    setMenu(rMenu);

    const promises = [loadConfigFromRemote()];

    if (includesOrders) {
      promises.push(
        findSpotOrdersFromRemote().catch((error) =>
          error.response?.status !== 401 ? null : error
        )
      );
    }

    if (includesImages) {
      promises.push(loadMenuImages());
    }

    if (includesFonts) {
      promises.push(loadMenuFonts());
    }

    if (shouldWait) {
      promises.push(wait(menuLoadingDelay));
    }

    const responses = await Promise.all(promises);
    const cConfig = _.clone(configRef.current) ?? {};
    _.merge(cConfig, responses[0]?.data ?? {});

    if (!_.isEqual(cConfig, configRef.current)) {
      setConfig(cConfig);
    }

    if (includesOrders) {
      const rOrders = responses[1]?.data;
      setOrders(rOrders);

      const firstSpot = _.first(rOrders)?.bill?.spot;
      if (spotRef.current != null && spotRef.current.id !== firstSpot?.id) {
        setLoadedSpot(firstSpot);
      } else if (firstSpot != null) {
        setSpot(firstSpot);
      }
    }

    loadingRef.current = false;
  }

  async function loadLanguage() {
    const response = await findLanguagesFromRemote();
    const rLanguages = response?.data;
    setLanguages(rLanguages);

    if (
      languageRef.current == null ||
      rLanguages?.find((l) => l.id === languageRef.current.id) == null
    ) {
      // TODO: Cebola, provavelmente aqui você vai pegar o idioma do navegador
      // com a função da lib que você está usando.
      // eslint-disable-next-line no-undef
      const defaultLanguage = navigator.language;
      // console.log('navigator.language', defaultLanguage);

      // TODO: Cebola, esse trecho mosta como setar o idioma. Quando o usuário
      // selecionar o idioma (imagino que você vá criar isso ainda), chame esse
      // selectLanguage e o array de idiomas suportados que estará salvo no storage,
      // que ficará um pouco diferente do que fiz aqui. Será assim onde você
      // for usar:
      //
      // setLanguage(selectLanguage(selectedLanguage, languages));
      //
      setLanguage(selectLanguage(defaultLanguage, rLanguages));
    }
  }

  function parseMenu(menu) {
    if (menu == null) return null;
    const result = _.cloneDeep(menu);

    result?.categories?.forEach((category) => {
      category.images = category?.items
        ?.filter((i) => i.image?.url != null)
        .map((i) => ({
          ...i.image,
          item: i,
        }));
    });

    return result;
  }

  function getDefaultMenuLogoUrl() {
    // const slug = menuSlugRef.current;
    // if (slug == null) return null;
    // return `https://cdn.jsdelivr.net/gh/menurapp/public/establishments/${slug}/layout/${slug}-banner.png`;

    return `https://link.menur.app/menu/logo_default`;
  }

  function getDefaultMenuTitleBackgroundUrl() {
    const slug = menuSlugRef.current;
    if (slug == null) return null;
    return `https://res.cloudinary.com/menur/image/upload/v1647828024/layout/title.png`;
  }

  function getMenuLogo() {
    let logo = menuLayoutRef.current?.logo;
    if (logo == null) logo = {};

    if (_.isEmpty(logo.url)) {
      logo.url = getDefaultMenuLogoUrl();
    }

    logo.url = transform(logo.url);
    return logo;
  }

  function getMenuTitleBackgroundUrl() {
    // let url = menuLayoutRef.current?.image?.title_url;

    // if (_.isEmpty(url)) {
    const url = getDefaultMenuTitleBackgroundUrl();
    // }

    return transform(url);
  }

  function getWhatsAppLink(text) {
    return `https://wa.me/55${
      menuInfoRef.current?.whats_app?.number ?? '00000000'
    }/${text ?? ''}`;
  }

  return {
    loadMenu,
    getMenuLogo,
    getMenuTitleBackgroundUrl,
    getWhatsAppLink,
  };
}

export { menuLoadingDelay, useMenuManager };
